// app_client/src/pages/admin/TransactionManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useTransactionStore from '../../store/useTransactionStore';
import useAuthStore from '../../store/authStore';
import useFinancialStore from '../../store/useFinancialStore';
import TransactionModal from '../../components/modals/TransactionModal';
import TransactionDetailModal from '../../components/accounts/TransactionDetailModal';
import TransactionList from '../../components/transactions/TransactionList';
import AllocationModal from '../../components/transactions/AllocationModal';
import Pagination from '../../components/common/Pagination';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';

const TransactionManagement = () => {
  const { transactions, totalPages, currentPage, fetchTransactions, deleteTransaction, loading } = useTransactionStore();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const { confirm } = useConfirmation();
  const { accounts: financialAccounts, fetchAccounts } = useFinancialStore();
  const canClearWrite = hasPermission('invoiceClearing', 'write');
  const canClearDelete = hasPermission('invoiceClearing', 'delete');
  const canDeleteTransaction = hasPermission('transaction', 'delete');
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [selectedTxId, setSelectedTxId] = useState(null);
  const [selectedAllocationTx, setSelectedAllocationTx] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ financialAccountId: '', startDate: '', endDate: '', type: 'all' });
  const transactionQuery = useMemo(() => ({
    search: searchTerm,
    page: 1,
    ...filters,
  }), [searchTerm, filters]);
  const fetchTransactionsRef = useRef(fetchTransactions);

  useEffect(() => {
    fetchTransactionsRef.current = fetchTransactions;
  }, [fetchTransactions]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTransactionsRef.current(transactionQuery);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [transactionQuery]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const formatCurrency = (val, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(val || 0);
  };

  const handleRowClick = (transactionId) => {
    setSelectedTxId(transactionId);
    setIsDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedTxId(null);
  };

  const handleOpenAllocationModal = (transaction) => {
    if (!canClearWrite) return;
    setSelectedAllocationTx(transaction);
    setIsAllocationModalOpen(true);
  };

  const handleAllocationModalClose = () => {
    setIsAllocationModalOpen(false);
    setSelectedAllocationTx(null);
  };

  const handleDeleteTransaction = async (transaction) => {
    if (!canDeleteTransaction || !transaction?._id) return;

    const confirmed = await confirm({
      title: 'Hareketi Sil',
      message: 'Bu hesap hareketini silmek istediğinize emin misiniz? Kasa/banka bakiyesi geri alınacaktır.',
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      requireReason: true,
      reasonLabel: 'Silme nedeni',
      reasonPlaceholder: 'Örn: Hatalı kayıt, mükerrer işlem...'
    });

    if (!confirmed) return;

    const result = await deleteTransaction(transaction._id, confirmed.reason);
    if (result.success) {
      toast.success('Hareket silindi ve bakiyeler güncellendi.');
      return;
    }

    toast.error(result.message || 'Hareket silinemedi.', { duration: 6000 });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 bg-slate-50/50 p-6 rounded-[2.5rem] border border-slate-100">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Hesap Hareketleri</h1>
          <p className="text-slate-500 text-sm font-medium">Finansal operasyon merkezi</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 flex-1 lg:justify-end">
          <div className="relative w-full max-w-xs">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${loading ? 'text-indigo-500' : 'text-slate-400'}`} size={16} />
            <input 
              type="text" placeholder="Ara..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white shadow-sm outline-none text-sm font-bold focus:ring-2 focus:ring-indigo-100 transition-all"
            />
            {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-500 animate-spin" size={16} />}
          </div>
          <button onClick={() => setIsCreateModalOpen(true)} className="bg-slate-900 text-white px-8 py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-xl active:scale-95">
            <Plus size={18} className="inline mr-1" /> Yeni İşlem
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-3xl border border-slate-100 shadow-sm flex flex-wrap items-center gap-4">
        <select name="financialAccountId" value={filters.financialAccountId} onChange={handleFilterChange} className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none">
          <option value="">Tüm Kasalar/Bankalar</option>
          {financialAccounts.map(acc => <option key={acc._id} value={acc._id}>{acc.name} ({acc.currency})</option>)}
        </select>
        <div className="flex items-center gap-2">
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none" />
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="bg-slate-50 rounded-xl px-4 py-2 text-xs font-bold text-slate-600 outline-none" />
        </div>
        <div className="flex gap-1 bg-slate-50 p-1 rounded-xl ml-auto">
          {['all', 'Inflow', 'Outflow', 'Transfer'].map(t => (
            <button key={t} onClick={() => setFilters(prev => ({ ...prev, type: t }))} className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${filters.type === t ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>
              {t === 'all' ? 'Tümü' : t === 'Inflow' ? 'Giriş' : t === 'Outflow' ? 'Çıkış' : 'Virman'}
            </button>
          ))}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <TransactionList
            transactions={transactions}
            loading={loading}
            formatCurrency={formatCurrency}
            searchTerm={searchTerm}
            onRowClick={handleRowClick}
            onAllocate={handleOpenAllocationModal}
            canAllocate={canClearWrite}
            onDelete={handleDeleteTransaction}
            canDelete={canDeleteTransaction}
          />
        </div>
        <div className="p-6 border-t border-slate-50 bg-slate-50/20">
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={(p) => fetchTransactions({ page: p, search: searchTerm, ...filters })} 
          />
        </div>
      </div>

      <TransactionModal 
        isOpen={isCreateModalOpen} 
        onClose={() => setIsCreateModalOpen(false)}
        accountId={null}
        key={isCreateModalOpen ? 'global-modal' : 'closed'}
      />

      <TransactionDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleDetailModalClose}
        transactionId={selectedTxId}
        onUpdated={() => fetchTransactions({ search: searchTerm, page: currentPage, ...filters })}
        canUnlink={canClearDelete}
      />

      <AllocationModal
        isOpen={isAllocationModalOpen}
        onClose={handleAllocationModalClose}
        transaction={selectedAllocationTx}
        onSuccess={() => fetchTransactions({ search: searchTerm, page: currentPage, ...filters })}
        canSubmit={canClearWrite}
      />
    </div>
  );
};

export default TransactionManagement;
